import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import { ownerApi } from "@/lib/api";
import { Spinner } from "@/components/common/States";
import { Switch } from "@/components/ui/switch";

export default function OwnerPromos() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["ownerPromos"], queryFn: ownerApi.getPromos });
  const [form, setForm] = useState(null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["ownerPromos"] });

  const create = useMutation({
    mutationFn: () => ownerApi.createPromo({ code: form.code.trim().toUpperCase(), kind: form.kind, value: Number(form.value), min_order: Number(form.min_order) || 0, first_order_only: form.first_order_only, expires_at: null, max_uses: form.max_uses ? Number(form.max_uses) : null }),
    onSuccess: () => { invalidate(); setForm(null); toast.success("Promo created"); },
    onError: (e) => toast.error(e.message),
  });

  if (query.isPending) return <Spinner />;

  return (
    <div className="space-y-4">
      <button onClick={() => setForm({ code: "", kind: "percent", value: 10, min_order: 0, first_order_only: false, max_uses: "" })} className="inline-flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"><Plus className="h-4 w-4" /> New promo</button>
      <div className="grid gap-3 sm:grid-cols-2">
        {query.data.map((p) => (
          <div key={p.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
            <div>
              <p className="font-display font-bold">{p.code}</p>
              <p className="text-xs text-muted-foreground">{p.kind === "percent" ? `${p.value}% off` : `₹${p.value} off`}{p.min_order ? ` · min ₹${p.min_order}` : ""}{p.first_order_only ? " · first order" : ""} · used {p.used_count}{p.max_uses ? `/${p.max_uses}` : ""}</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={p.active} onCheckedChange={(v) => ownerApi.setPromoActive(p.id, v).then(invalidate)} />
              <button onClick={() => ownerApi.deletePromo(p.id).then(invalidate)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {form ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setForm(null)}>
          <div className="w-full max-w-sm space-y-3 rounded-2xl bg-card p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold">New promo</h3>
            <input placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="w-full rounded-xl border border-border p-2.5 text-sm uppercase" />
            <div className="flex gap-2">
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="flex-1 rounded-xl border border-border bg-background p-2.5 text-sm"><option value="percent">% off</option><option value="fixed">₹ off</option></select>
              <input type="number" placeholder="Value" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="w-24 rounded-xl border border-border p-2.5 text-sm" />
            </div>
            <input type="number" placeholder="Min order (₹)" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} className="w-full rounded-xl border border-border p-2.5 text-sm" />
            <input type="number" placeholder="Max uses (blank = ∞)" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="w-full rounded-xl border border-border p-2.5 text-sm" />
            <label className="flex items-center justify-between text-sm">First order only <Switch checked={form.first_order_only} onCheckedChange={(v) => setForm({ ...form, first_order_only: v })} /></label>
            <button onClick={() => create.mutate()} disabled={!form.code || create.isPending} className="w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">Create</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
